import { useState, useEffect } from "react";
import { loadData, storeData, getCached } from "./s3Data";
import { useIsMobile } from "../useIsMobile";

const STATUS_META = {
  Planned:     { color: "#555",    bg: "#1a1a1d", label: "Planned" },
  "In Progress": { color: "#378ADD", bg: "#0e1820", label: "In Progress" },
  Done:        { color: "#1D9E75", bg: "#0a1a12", label: "Done" },
};

const P = {
  root: {
    fontFamily: "'DM Mono', 'Courier New', monospace",
    background: "#0f0f11",
    minHeight: "100vh",
    color: "#e8e6e0",
    paddingTop: 70,
  },
  page: {
    padding: "32px 28px",
    maxWidth: 780,
    margin: "0 auto",
  },
  title: {
    fontFamily: "'Fraunces', Georgia, serif",
    fontSize: 26,
    fontWeight: 600,
    color: "#f0ede6",
    letterSpacing: "-0.02em",
    margin: 0,
    marginBottom: 4,
  },
  sub: {
    fontSize: 10,
    color: "#333",
    letterSpacing: "0.1em",
    textTransform: "uppercase",
    marginBottom: 28,
  },
  addCard: {
    background: "#111113",
    border: "1px dashed #2a2a2e",
    borderRadius: 12,
    padding: "20px 22px",
    marginBottom: 20,
  },
  label: {
    display: "block",
    fontSize: 9,
    color: "#444",
    letterSpacing: "0.1em",
    textTransform: "uppercase",
    marginBottom: 5,
    marginTop: 12,
  },
  input: {
    width: "100%",
    background: "#111113",
    border: "1px solid #2a2a2e",
    borderRadius: 7,
    padding: "8px 11px",
    color: "#e8e6e0",
    fontSize: 13,
    fontFamily: "'DM Mono', monospace",
    outline: "none",
    boxSizing: "border-box",
  },
  textarea: {
    width: "100%",
    background: "#111113",
    border: "1px solid #2a2a2e",
    borderRadius: 7,
    padding: "8px 11px",
    color: "#e8e6e0",
    fontSize: 12,
    fontFamily: "'DM Mono', monospace",
    outline: "none",
    boxSizing: "border-box",
    resize: "vertical",
    minHeight: 64,
    lineHeight: 1.5,
  },
  select: {
    width: "100%",
    background: "#111113",
    border: "1px solid #2a2a2e",
    borderRadius: 7,
    padding: "8px 11px",
    color: "#e8e6e0",
    fontSize: 13,
    fontFamily: "'DM Mono', monospace",
    outline: "none",
    cursor: "pointer",
    boxSizing: "border-box",
  },
  btn: (variant) => ({
    background: variant === "primary" ? "#1D9E75" : "transparent",
    border: "1px solid " + (variant === "primary" ? "transparent" : variant === "danger" ? "#2a1212" : "#2a2a2e"),
    borderRadius: 7,
    color: variant === "primary" ? "#fff" : variant === "danger" ? "#E24B4A" : "#555",
    fontSize: 11,
    padding: "7px 14px",
    cursor: "pointer",
    fontFamily: "'DM Mono', monospace",
    letterSpacing: "0.04em",
    whiteSpace: "nowrap",
  }),
  projectCard: (done) => ({
    background: done ? "#0d0d0f" : "#161618",
    border: "1px solid " + (done ? "#1a1a1d" : "#2a2a2e"),
    borderRadius: 12,
    padding: "18px 20px",
    marginBottom: 10,
    opacity: done ? 0.6 : 1,
    transition: "opacity 0.2s",
  }),
  priorityNum: {
    fontSize: 10,
    color: "#2a2a2e",
    letterSpacing: "0.06em",
    fontWeight: 600,
    marginBottom: 6,
  },
  projectName: (done) => ({
    fontSize: 15,
    fontWeight: 600,
    color: done ? "#555" : "#f0ede6",
    textDecoration: done ? "line-through" : "none",
    marginBottom: 4,
  }),
  projectDesc: {
    fontSize: 12,
    color: "#555",
    lineHeight: 1.6,
    marginBottom: 10,
    whiteSpace: "pre-wrap",
  },
  bottomRow: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    flexWrap: "wrap",
  },
  statusBadge: (status) => ({
    fontSize: 10,
    padding: "3px 10px",
    borderRadius: 20,
    background: STATUS_META[status]?.bg || "#1a1a1d",
    color: STATUS_META[status]?.color || "#555",
    letterSpacing: "0.04em",
    border: "1px solid " + (STATUS_META[status]?.color || "#555") + "33",
  }),
  costBadge: {
    fontSize: 10,
    color: "#444",
    padding: "3px 10px",
    borderRadius: 20,
    background: "#111113",
    border: "1px solid #1e1e22",
  },
  orderBtns: {
    display: "flex",
    gap: 4,
    marginLeft: "auto",
  },
  orderBtn: {
    background: "#111113",
    border: "1px solid #1e1e22",
    borderRadius: 5,
    color: "#333",
    fontSize: 12,
    padding: "2px 8px",
    cursor: "pointer",
    lineHeight: 1.4,
    fontFamily: "'DM Mono', monospace",
  },
  editArea: {
    marginTop: 14,
    paddingTop: 14,
    borderTop: "1px solid #1e1e22",
  },
};

const BLANK = { title: "", description: "", cost: "", status: "Planned" };

export default function Projects() {
  const isMobile = useIsMobile();
  const [projects, setProjects] = useState(() => getCached("projects.json", []));
  const [saveStatus, setSaveStatus] = useState(null);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ ...BLANK });
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});

  useEffect(() => {
    loadData("projects.json", null).then(d => { if (d !== null) setProjects(d); }).catch(() => {});
  }, []);

  async function persist(next) {
    setSaveStatus("saving");
    try {
      await storeData("projects.json", next);
      setSaveStatus("saved");
      setTimeout(() => setSaveStatus(null), 1500);
    } catch (e) {
      setSaveStatus("error: " + e.name);
    }
  }

  function addProject() {
    const title = form.title.trim();
    if (!title) return;
    const next = [
      { id: Date.now(), title, description: form.description.trim(), cost: form.cost, status: form.status },
      ...projects,
    ];
    setProjects(next);
    setForm({ ...BLANK });
    setAdding(false);
    persist(next);
  }

  function remove(id) {
    const next = projects.filter(p => p.id !== id);
    setProjects(next);
    persist(next);
  }

  function moveUp(idx) {
    if (idx === 0) return;
    const next = [...projects];
    [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
    setProjects(next);
    persist(next);
  }

  function moveDown(idx) {
    if (idx === projects.length - 1) return;
    const next = [...projects];
    [next[idx], next[idx + 1]] = [next[idx + 1], next[idx]];
    setProjects(next);
    persist(next);
  }

  function startEdit(p) {
    setEditingId(p.id);
    setEditForm({ title: p.title, description: p.description || "", cost: p.cost || "", status: p.status });
  }

  function saveEdit(id) {
    const title = editForm.title.trim();
    if (!title) return;
    const next = projects.map(p => p.id === id ? { ...p, ...editForm, title } : p);
    setProjects(next);
    setEditingId(null);
    persist(next);
  }

  function setStatus(id, status) {
    const next = projects.map(p => p.id === id ? { ...p, status } : p);
    setProjects(next);
    persist(next);
  }

  const active = projects.filter(p => p.status !== "Done");
  const done = projects.filter(p => p.status === "Done");

  function renderProject(p, idx, allItems) {
    const isEditing = editingId === p.id;
    return (
      <div key={p.id} style={P.projectCard(p.status === "Done")}>
        <div style={P.priorityNum}>#{allItems.indexOf(p) + 1}</div>

        {isEditing ? (
          <div style={P.editArea}>
            <label style={{ ...P.label, marginTop: 0 }}>Title</label>
            <input style={P.input} value={editForm.title} onChange={e => setEditForm(f => ({ ...f, title: e.target.value }))} />
            <label style={P.label}>Description</label>
            <textarea style={P.textarea} value={editForm.description} onChange={e => setEditForm(f => ({ ...f, description: e.target.value }))} placeholder="Details, scope, materials needed…" />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <div>
                <label style={P.label}>Est. Cost</label>
                <input style={P.input} value={editForm.cost} onChange={e => setEditForm(f => ({ ...f, cost: e.target.value }))} placeholder="e.g. $500, $2k–$4k" />
              </div>
              <div>
                <label style={P.label}>Status</label>
                <select style={P.select} value={editForm.status} onChange={e => setEditForm(f => ({ ...f, status: e.target.value }))}>
                  {Object.keys(STATUS_META).map(s => <option key={s}>{s}</option>)}
                </select>
              </div>
            </div>
            <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
              <button style={P.btn("primary")} onClick={() => saveEdit(p.id)}>Save</button>
              <button style={P.btn()} onClick={() => setEditingId(null)}>Cancel</button>
            </div>
          </div>
        ) : (
          <>
            <p style={P.projectName(p.status === "Done")}>{p.title}</p>
            {p.description && <p style={P.projectDesc}>{p.description}</p>}
            <div style={P.bottomRow}>
              <span style={P.statusBadge(p.status)}>{p.status}</span>
              {p.cost && <span style={P.costBadge}>{p.cost}</span>}
              <div style={P.orderBtns}>
                <select
                  style={{ ...P.select, width: "auto", padding: "2px 6px", fontSize: 10 }}
                  value={p.status}
                  onChange={e => setStatus(p.id, e.target.value)}
                  title="Change status"
                >
                  {Object.keys(STATUS_META).map(s => <option key={s}>{s}</option>)}
                </select>
                <button style={P.orderBtn} onClick={() => moveUp(idx)} title="Move up" disabled={idx === 0}>↑</button>
                <button style={P.orderBtn} onClick={() => moveDown(idx)} title="Move down" disabled={idx === allItems.length - 1}>↓</button>
                <button style={{ ...P.btn(), padding: "2px 8px", fontSize: 10 }} onClick={() => startEdit(p)}>Edit</button>
                <button style={{ ...P.btn("danger"), padding: "2px 8px", fontSize: 10 }} onClick={() => remove(p.id)}>✕</button>
              </div>
            </div>
          </>
        )}
      </div>
    );
  }

  return (
    <div style={P.root}>
      <div style={{ ...P.page, padding: isMobile ? "20px 12px" : P.page.padding }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
          <p style={P.title}>Home Projects</p>
          {!adding && (
            <button style={P.btn("primary")} onClick={() => setAdding(true)}>+ Add Project</button>
          )}
        </div>
        <p style={P.sub}>
          {saveStatus === "saving" ? "Saving…"
            : saveStatus === "saved" ? "Saved"
            : saveStatus === "error" ? "Save failed"
            : `${active.length} active · ${done.length} done`}
        </p>

        {/* Add form */}
        {adding && (
          <div style={P.addCard}>
            <label style={{ ...P.label, marginTop: 0 }}>Project Title</label>
            <input
              style={P.input}
              autoFocus
              value={form.title}
              onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
              onKeyDown={e => e.key === "Enter" && !e.shiftKey && addProject()}
              placeholder="e.g. Repaint living room, Fix deck boards…"
            />
            <label style={P.label}>Description / Notes</label>
            <textarea
              style={P.textarea}
              value={form.description}
              onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              placeholder="Scope, materials, contractor info…"
            />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <div>
                <label style={P.label}>Est. Cost (optional)</label>
                <input
                  style={P.input}
                  value={form.cost}
                  onChange={e => setForm(f => ({ ...f, cost: e.target.value }))}
                  placeholder="e.g. $500, $2k–$4k"
                />
              </div>
              <div>
                <label style={P.label}>Status</label>
                <select style={P.select} value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))}>
                  {Object.keys(STATUS_META).map(s => <option key={s}>{s}</option>)}
                </select>
              </div>
            </div>
            <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
              <button style={P.btn("primary")} onClick={addProject}>Add to Top</button>
              <button style={P.btn()} onClick={() => { setAdding(false); setForm({ ...BLANK }); }}>Cancel</button>
            </div>
          </div>
        )}

        {projects.length === 0 && !adding && (
          <div style={{ textAlign: "center", padding: "56px 0", color: "#222", fontSize: 13 }}>
            No projects yet.<br />
            <span style={{ fontSize: 11 }}>Track home improvement projects by priority — painting, repairs, renovations.</span>
          </div>
        )}

        {/* Active projects */}
        {active.map((p, i) => renderProject(p, i, active))}

        {/* Done projects */}
        {done.length > 0 && (
          <>
            <div style={{ fontSize: 9, color: "#2a2a2e", letterSpacing: "0.12em", textTransform: "uppercase", margin: "20px 0 10px", paddingTop: 16, borderTop: "1px solid #1a1a1d" }}>
              Completed ({done.length})
            </div>
            {done.map((p, i) => renderProject(p, i, done))}
          </>
        )}
      </div>
    </div>
  );
}
